import { ChangeEvent, useCallback, useContext, useMemo, useState } from 'react';
import { SearchInput, SortDescriptor } from '@ds';
import { DataTable } from '@ui';
import { toast } from 'react-toastify';
import { AuthContext } from '@lp/core/auth';
import { CaseAccessTicketItem } from '@/api/apis/case';
import { ArchiveCasesPermissions } from '@/permissions';
import { retrieveUnifiedError } from '@/api/utils';
import {
  useAccessTicketsRevoke,
  useAccessTicketsRevokeInfinite,
  useCaseAccessTicketsQuery,
  useGrantArchiveAccessTicketsInfiniteMutation,
  useGrantArchiveAccessTicketsMutation,
} from '@/queries/case';
import {
  CaseAccessActions,
  CaseAccessLoadingState,
  getRenderCaseAccessTableCell,
} from './GrantAccessTable/getRenderCaseAccessTableCell';
import {
  accessColumns,
  CASE_ACCESS_COLUMNS,
  CaseAccessTabProps,
  DEFAULT_ITEMS_PER_PAGE,
  getUserDisplayName,
} from './GrantAccessTable/config';
import { sortCaseAccessTickets } from './GrantAccessTable/utils';

type TableState = {
  searchText: string;
  page: number;
  itemsPerPage: number;
  sortBy?: SortDescriptor;
};

const defaultTableState: TableState = {
  searchText: '',
  page: 1,
  itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
  sortBy: {
    column: accessColumns.accessExpirationDate.key,
    direction: 'ascending',
  },
};

const CaseAccessTab = ({ caseInfo }: CaseAccessTabProps) => {
  const { checkPermission } = useContext(AuthContext);
  const { caseId } = caseInfo;

  const [loading, setLoading] = useState<CaseAccessLoadingState>(null);
  const [tableState, setTableState] = useState<TableState>(defaultTableState);

  const updateTableState = useCallback((nextState: Partial<TableState>) => {
    setTableState((prev) => ({
      ...prev,
      ...nextState,
    }));
  }, []);

  const { data, isLoading, isFetching } = useCaseAccessTicketsQuery(caseId);

  const canGrant90Permission = checkPermission(ArchiveCasesPermissions.CaseArchiveAccess90DaysCreate);
  const canGrantIndefPermission = checkPermission(ArchiveCasesPermissions.CaseArchiveAccessIndefiniteCreate);

  const canRevokePermission =
    checkPermission(ArchiveCasesPermissions.CaseArchiveAccess90DaysDelete) ||
    checkPermission(ArchiveCasesPermissions.CaseArchiveAccessIndefiniteDelete);

  const canGrantArchiveAccess =
    checkPermission(ArchiveCasesPermissions.CaseArchiveAccess90DaysCreate) ||
    checkPermission(ArchiveCasesPermissions.CaseArchiveAccess90DaysDelete) ||
    checkPermission(ArchiveCasesPermissions.CaseArchiveAccessIndefiniteCreate) ||
    checkPermission(ArchiveCasesPermissions.CaseArchiveAccessIndefiniteDelete);

  const columns = useMemo(
    () =>
      canGrantArchiveAccess
        ? CASE_ACCESS_COLUMNS
        : CASE_ACCESS_COLUMNS.filter((column) => column.key !== accessColumns.grantAccess.key),
    [canGrantArchiveAccess],
  );

  const { mutateAsync: grant90Async } = useGrantArchiveAccessTicketsMutation();
  const { mutateAsync: grantIndefAsync } = useGrantArchiveAccessTicketsInfiniteMutation();
  const { mutateAsync: revoke90Async } = useAccessTicketsRevoke();
  const { mutateAsync: revokeIndefAsync } = useAccessTicketsRevokeInfinite();

  const filteredData = useMemo((): CaseAccessTicketItem[] => {
    if (!data) return [];

    const term = tableState.searchText.trim().toLowerCase();

    if (!term) return data;

    return data.filter((item) => {
      const { firstName, lastName } = item.forUser;

      const firstNameText = firstName?.toLowerCase() ?? '';
      const lastNameText = lastName?.toLowerCase() ?? '';
      const fullNameText = `${firstNameText} ${lastNameText}`.trim();

      return firstNameText.includes(term) || lastNameText.includes(term) || fullNameText.includes(term);
    });
  }, [data, tableState.searchText]);

  const sortedData = useMemo(
    () => sortCaseAccessTickets(filteredData, tableState.sortBy),
    [filteredData, tableState.sortBy],
  );

  const totalItems = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / tableState.itemsPerPage));

  const paginatedData = useMemo(() => {
    const start = (tableState.page - 1) * tableState.itemsPerPage;
    const end = start + tableState.itemsPerPage;

    return sortedData.slice(start, end);
  }, [sortedData, tableState.page, tableState.itemsPerPage]);

  const onGrant90 = useCallback(
    async (item: CaseAccessTicketItem) => {
      try {
        const { id: forUserId } = item.forUser;

        setLoading({ userId: forUserId, action: 'grant90' });

        await grant90Async({ caseId, forUserId });

        toast.success(`Granted 60-day access for ${getUserDisplayName(item.forUser)}`);
      } catch (e) {
        toast.error(retrieveUnifiedError(e).message);
      } finally {
        setLoading(null);
      }
    },
    [caseId, grant90Async],
  );

  const onGrantIndefinite = useCallback(
    async (item: CaseAccessTicketItem) => {
      try {
        const { id: forUserId } = item.forUser;

        setLoading({ userId: forUserId, action: 'grantIndefinite' });

        await grantIndefAsync({ caseId, forUserId });

        toast.success(`Granted indefinite access for ${getUserDisplayName(item.forUser)}`);
      } catch (e) {
        toast.error(retrieveUnifiedError(e).message);
      } finally {
        setLoading(null);
      }
    },
    [caseId, grantIndefAsync],
  );

  const onRevoke = useCallback(
    async (item: CaseAccessTicketItem) => {
      try {
        const { forUser, isInfinite } = item;

        setLoading({ userId: forUser.id, action: 'revoke' });

        if (isInfinite) {
          await revokeIndefAsync({ caseId, userId: forUser.id });
        } else {
          await revoke90Async({ caseId, userId: forUser.id });
        }

        toast.success(`Revoked access for ${getUserDisplayName(forUser)}`);
      } catch (e) {
        toast.error(retrieveUnifiedError(e).message);
      } finally {
        setLoading(null);
      }
    },
    [caseId, revoke90Async, revokeIndefAsync],
  );

  const actions: CaseAccessActions = useMemo(
    () => ({
      onGrant90,
      onGrantIndefinite,
      onRevoke,
    }),
    [onGrant90, onGrantIndefinite, onRevoke],
  );

  const renderCell = useMemo(
    () =>
      getRenderCaseAccessTableCell({
        actions,
        loading,
        canGrant90: canGrant90Permission,
        canGrantIndefinite: canGrantIndefPermission,
        canRevoke: canRevokePermission,
      }),
    [actions, loading, canGrant90Permission, canGrantIndefPermission, canRevokePermission],
  );

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateTableState({
      searchText: event.target.value,
      page: 1,
    });
  };

  const onSortByChange = useCallback(
    (next: SortDescriptor) => {
      updateTableState({
        sortBy: next,
        page: 1,
      });
    },
    [updateTableState],
  );

  const onItemsPerPageChange = useCallback(
    (nextItemsPerPage: number) => {
      updateTableState({
        itemsPerPage: nextItemsPerPage,
        page: 1,
      });
    },
    [updateTableState],
  );

  return (
    <div className="flex flex-col place-items-stretch gap-6">
      <h1 className="text-hm text-base-5">Access</h1>

      <div className="max-w-[285px] grow">
        <SearchInput
          placeholder="Search"
          value={tableState.searchText}
          inputVariant="secondary"
          onChange={onSearchChange}
        />
      </div>

      <div className="w-[960px] flex flex-col gap-4">
        <DataTable
          type="default"
          label="Case access"
          columns={columns}
          renderCell={renderCell}
          data={paginatedData}
          isLoading={isLoading}
          isFetching={isFetching}
          totalItems={totalItems}
          totalPages={totalPages}
          page={tableState.page}
          itemsPerPage={tableState.itemsPerPage}
          onPageChange={(nextPage) => updateTableState({ page: nextPage })}
          onItemsPerPageChange={onItemsPerPageChange}
          sortBy={tableState.sortBy}
          onSortByChange={onSortByChange}
        />
      </div>
    </div>
  );
};

export default CaseAccessTab;
